export const getMatrizClienteDashboardTemplate = (images, tablas, info) => {

    const COLORS = {
        primary: '#1e293b',
        secondary: '#64748b',
        border: '#cbd5e1',
        bgPanel: '#f8fafc',
        bgTableHeader: '#f1f5f9',
        green: '#16a34a',
        red: '#dc2626',
        orange: '#ea580c',
        blue: '#2563eb',
        aceptados: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'],
        alertas: ['#3b82f6', '#ef4444', '#f97316']
    };

    // Dimensiones calculadas para A3 Landscape (Ancho útil: 1110px)
    const DIMS = {
        gap: 15,
        kpiWidth4: 266,   // Para 4 columnas
        kpiHeight: 95,    // Ligeramente más alto para dar respiro
        riskPanelW: 547,  // Ancho de los paneles de riesgo (mitad - gap)
        riskPanelH: 340,
        chartEvolH: 450
    };

    // Tarjeta con imagen (KPIs y Gráficos)
    const createCleanCard = (imageKey, height, width, leftBorderColor = null, innerMargin = 4) => {
        const imageBase64 = images[imageKey];

        const canvasElements = [
            { type: 'rect', x: 0, y: 0, w: width, h: height, r: 6, lineColor: COLORS.border, lineWidth: 1, color: '#ffffff' }
        ];

        if (leftBorderColor) {
            canvasElements.push({
                type: 'rect', x: 0, y: 0, w: 5, h: height, r: 0, color: leftBorderColor, lineColor: leftBorderColor
            });
        }

        return {
            stack: [
                { canvas: canvasElements },
                {
                    margin: [0, -height, 0, 0],
                    table: {
                        widths: [width],
                        heights: [height],
                        body: [
                            [
                                {
                                    stack: [
                                        imageBase64 ? {
                                            image: imageBase64,
                                            fit: [width - innerMargin, height - innerMargin],
                                            alignment: 'center',
                                        } : {
                                            text: 'Cargando datos...', style: 'muted', alignment: 'center', margin: [0, height / 3, 0, 0]
                                        }
                                    ],
                                    verticalAlign: 'middle',
                                    border: [false, false, false, false],
                                }
                            ]
                        ]
                    },
                    layout: 'noBorders'
                }
            ],
            unbreakable: true
        };
    };

    // Encabezado de Sección
    const createSectionTitle = (title, color) => ({
        columns: [
            { canvas: [{ type: 'rect', x: 0, y: 4, w: 8, h: 8, r: 4, color: color }], width: 15 },
            { text: title, style: 'sectionTitle' }
        ],
        margin: [0, 10, 0, 8]
    });

    // Tablas Nativas para Inherente/Residual
    const buildDataTable = (data) => {
        if (!data || data.length === 0) return { text: 'Sin datos', style: 'muted', alignment: 'center', margin: [0, 20] };

        const body = [
            [
                { text: 'Nivel', style: 'tableHeader', alignment: 'left' },
                { text: 'No. Clientes', style: 'tableHeader', alignment: 'right' }
            ]
        ];

        data.forEach((row, i) => {
            const isPair = i % 2 === 0;
            body.push([
                { text: row.nivel || '-', style: 'tableCell', fillColor: isPair ? '#ffffff' : '#f8fafc', bold: true },
                { text: row.cantidad?.toString() || '0', style: 'tableCell', alignment: 'right', fillColor: isPair ? '#ffffff' : '#f8fafc' }
            ]);
        });

        return {
            table: {
                headerRows: 1,
                widths: ['*', 'auto'],
                body: body
            },
            layout: {
                hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 1 : 1,
                vLineWidth: () => 1,
                hLineColor: () => COLORS.border,
                vLineColor: () => COLORS.border,
                fillColor: (rowIndex) => rowIndex === 0 ? COLORS.bgTableHeader : null
            }
        };
    };

    // Construcción de Paneles de Riesgo (Fila 3)
    const buildRiskPanel = (title, imgGlobalKey, imgPieKey, tableData) => {
        return {
            width: DIMS.riskPanelW,
            stack: [
                {
                    canvas: [{ type: 'rect', x: 0, y: 0, w: DIMS.riskPanelW, h: DIMS.riskPanelH, r: 6, lineColor: COLORS.border, color: '#ffffff' }],
                },
                {
                    // Desplazamos el contenido hacia arriba respetando el height
                    margin: [15, -(DIMS.riskPanelH - 10), 15, 0],
                    stack: [
                        { text: title, alignment: 'center', fontSize: 11, bold: true, color: COLORS.secondary, margin: [0, 0, 0, 15] },
                        {
                            columns: [
                                {
                                    width: 190,
                                    stack: [
                                        { text: `Riesgo Global ${title.split(' ')[1]}`, fontSize: 9, bold: true, color: COLORS.secondary, alignment: 'center', margin: [0, 0, 0, 5] },
                                        createCleanCard(imgGlobalKey, 90, 190, null, 2),
                                        { text: '', margin: [0, 15, 0, 0] },
                                        buildDataTable(tableData)
                                    ]
                                },
                                { width: DIMS.gap + 10, text: '' },
                                {
                                    width: '*',
                                    stack: [
                                        createCleanCard(imgPieKey, 270, 310, null, 5)
                                    ]
                                }
                            ]
                        }
                    ]
                }
            ]
        };
    };

    const buildFiltersAnnex = (filtros) => {
        const tableBody = [];
        for (let i = 0; i < filtros.length; i += 3) {
            const row = [
                renderFiltro(filtros[i]),
                renderFiltro(filtros[i + 1]),
                renderFiltro(filtros[i + 2])
            ];
            tableBody.push(row);
        }

        return {
            table: {
                widths: ['*', '*', '*'],
                body: tableBody
            },
            layout: 'noBorders',
            margin: [0, 15, 0, 0]
        };
    };

    const renderFiltro = (filtro) => {
        if (!filtro) return { text: '' };

        const isArray = Array.isArray(filtro.value);
        const content = isArray
            ? { ul: filtro.value, fontSize: 9, color: filtro.isDefault ? COLORS.blue : COLORS.primary, italics: filtro.isDefault, margin: [0, 0, 0, 0] }
            : { text: String(filtro.value), fontSize: 9, color: filtro.isDefault ? COLORS.blue : COLORS.primary, italics: filtro.isDefault };

        return {
            stack: [
                { text: filtro.label.toUpperCase(), fontSize: 7, bold: true, color: COLORS.secondary, margin: [0, 0, 0, 2] },
                content
            ],
            margin: [0, 0, 10, 15]
        };
    };

    return {
        pageSize: 'A3',
        pageOrientation: 'landscape',
        pageMargins: [40, 85, 40, 30],

        header: {
            margin: [40, 20, 40, 0],
            columns: [
                {
                    width: 65,
                    ...(info.logo ? { image: info.logo, width: 55, alignment: 'left' } : { text: '' })
                },
                {
                    width: '*',
                    stack: [
                        { text: info.empresa, style: 'brandText' },
                        {
                            text: [
                                { text: 'Dashboard de Matriz de Cliente ', style: 'headerMeta' },
                                {
                                    text: info.vistaHistorica ? ' (Vista Histórica)' : '',
                                    color: info.vistaHistorica ? '#f59e0b' : '#3b82f6',
                                    bold: true,
                                    fontSize: 10
                                }
                            ],
                            margin: [0, 1, 0, 4]
                        },
                        {
                            text: [
                                { text: `Empresa: ${info.empresaSeleccionada}  |  Sector Económico: ${info.sectorSeleccionado}`, style: 'contextText' },
                                { text: `  |  Filtros Aplicados: ${info.totalFiltros}`, color: COLORS.secondary, bold: true, fontSize: 10 }
                            ]
                        }
                    ]
                },
                {
                    width: 'auto',
                    stack: [
                        { text: `Fecha de Generación: ${info.fechaGeneracion}`, style: 'headerMeta', alignment: 'right' },
                        { text: `Usuario: ${info.nombre_usuario}`, style: 'headerMeta', alignment: 'right', margin: [0, 2, 0, 0] }
                    ]
                }
            ]
        },

        // PIE DE PÁGINA
        footer: (currentPage, pageCount) => ({
            columns: [
                { text: 'Generado desde el Módulo de Matrices', style: 'footerText' },
                { text: `Página ${currentPage} de ${pageCount}`, style: 'footerText', alignment: 'right' }
            ],
            margin: [40, 10, 40, 0]
        }),

        content: [
            // FILA 1: Segmentación de clientes aceptados
            createSectionTitle('SEGMENTACIÓN DE CLIENTES ACEPTADOS', COLORS.green),
            {
                columns: [
                    createCleanCard('aceptados-0', DIMS.kpiHeight, DIMS.kpiWidth4, COLORS.aceptados[0]), { width: DIMS.gap, text: '' },
                    createCleanCard('aceptados-1', DIMS.kpiHeight, DIMS.kpiWidth4, COLORS.aceptados[1]), { width: DIMS.gap, text: '' },
                    createCleanCard('aceptados-2', DIMS.kpiHeight, DIMS.kpiWidth4, COLORS.aceptados[2]), { width: DIMS.gap, text: '' },
                    createCleanCard('aceptados-3', DIMS.kpiHeight, DIMS.kpiWidth4, COLORS.aceptados[3])
                ],
                margin: [0, 0, 0, 12]
            },

            // FILA 2: Segmentación por Alertas
            createSectionTitle('SEGMENTACIÓN DE CLIENTES POR ALERTAS', COLORS.red),
            {
                columns: [
                    { width: 141, text: '' }, // Espaciador izquierdo exacto
                    createCleanCard('alertas-0', DIMS.kpiHeight, DIMS.kpiWidth4, COLORS.alertas[0]), { width: DIMS.gap, text: '' },
                    createCleanCard('alertas-1', DIMS.kpiHeight, DIMS.kpiWidth4, COLORS.alertas[1]), { width: DIMS.gap, text: '' },
                    createCleanCard('alertas-2', DIMS.kpiHeight, DIMS.kpiWidth4, COLORS.alertas[2]),
                    { width: 141, text: '' }, // Espaciador derecho exacto
                ],
                margin: [0, 0, 0, 12]
            },

            // FILA 3: Riesgo Inherente y Residual
            createSectionTitle('CLIENTES POR NIVEL DE RIESGO', COLORS.orange),
            {
                columns: [
                    buildRiskPanel('RIESGO INHERENTE', 'kpi-global-inherente', 'pie-inherente', tablas.inherente),
                    { width: DIMS.gap, text: '' },
                    buildRiskPanel('RIESGO RESIDUAL', 'kpi-global-residual', 'pie-residual', tablas.residual)
                ],
                margin: [0, 0, 0, 0]
            },

            // FILA 4: Gráfico Temporal
            { text: '', pageBreak: 'before' },

            createSectionTitle('GRÁFICO TEMPORAL DE LOS RIESGOS', COLORS.blue),
            createCleanCard('line-evolucion', DIMS.chartEvolH, 1110),

            { text: '', pageBreak: 'before' },
            createSectionTitle('ANEXO: FILTROS DE BÚSQUEDA APLICADOS', COLORS.secondary),
            {
                text: 'A continuación se listan los filtros aplicados en el reporte (los valores por defecto se resaltan en color azul):',
                fontSize: 10, color: COLORS.secondary, margin: [0, 0, 0, 10]
            },
            buildFiltersAnnex(info.filtrosAplicados)
        ],

        styles: {
            brandText: { fontSize: 16, bold: true, color: COLORS.primary, margin: [0, 0, 0, 2] },
            headerMeta: { fontSize: 10, color: COLORS.secondary },
            contextText: { fontSize: 10, bold: true, color: COLORS.blue, background: '#eff6ff' }, // Resalta los filtros
            sectionTitle: { fontSize: 11, bold: true, color: COLORS.primary, characterSpacing: 0.5 },
            muted: { fontSize: 9, color: COLORS.secondary, italics: true },
            footerText: { fontSize: 9, color: COLORS.secondary },
            tableHeader: { fontSize: 9, bold: true, color: COLORS.primary, margin: [4, 5, 4, 5] },
            tableCell: { fontSize: 9, color: COLORS.primary, margin: [4, 5, 4, 5] }
        },
        defaultStyle: { font: 'Roboto' }
    };
};